import { createContext, useState, useEffect } from 'react';
import {
  useParams,
  Outlet,
  Navigate,
  useLoaderData,
  useRevalidator,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import Loader from 'components/Loader';

import { QUERY_KEYS } from '../../constants';
import labellingClientAPI from '../../clientAPI/labelling';
import propagate403Error from '../../utils/propagate403Error';

export type LabelProductContextProps = {
  totalProductAmount: number;
  labelledProductAmount: number;
  prevProductId: string;
  nextProductId: string;
  isCurrentLabelled: boolean;
  increaseLabelledAmount: () => void;
};

export const AllProductContext = createContext<LabelProductContextProps>({
  totalProductAmount: 0,
  labelledProductAmount: 0,
  prevProductId: '',
  nextProductId: '',
  isCurrentLabelled: false,
  increaseLabelledAmount: () => {
    return;
  },
});

export default function LabellingProduct() {
  const { datasetId, productId } = useParams();
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();
  const [labelledProductAmount, setLabelledAmount] = useState(0);
  const [unlabelledProductIds, setUnlabelledProductIds] = useState<
    Array<string>
  >([]);
  const [labelledProductIds, setLabelledProductIds] = useState<Array<string>>(
    [],
  );

  const { status, data, error } = useQuery({
    queryKey: [QUERY_KEYS.LABELLING_DATASET_PRODUCTS, datasetId],
    queryFn: async () =>
      labellingClientAPI.findDatasetProducts(
        datasetId!,
        loaderData.access_token,
      ),
    staleTime: Infinity,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  useEffect(() => {
    setLabelledAmount(data?.labeledIds.length || 0);
    setUnlabelledProductIds(
      data?.unlabeledIds.map((item) => item.threadId) || [],
    );
    setLabelledProductIds(data?.labeledIds.map((item) => item.threadId) || []);
  }, [data]);

  const increaseLabelledAmount = () => {
    if (!productId) return;
    const unLabelledIdx = unlabelledProductIds.findIndex(
      (id) => id === productId,
    );
    if (unLabelledIdx !== -1) {
      const newUnlabelledIdx = [...unlabelledProductIds];
      newUnlabelledIdx.splice(unLabelledIdx, 1);

      setLabelledAmount(labelledProductAmount + 1);
      setUnlabelledProductIds(newUnlabelledIdx);
      setLabelledProductIds(labelledProductIds.concat([productId]));
    }
  };

  propagate403Error(error as keyable);
  if (status === 'loading') return <Loader />;
  if (!data)
    return (
      <div className='tw-w-full tw-h-2/3 tw-flex tw-items-center tw-justify-center'>
        <h2 className='eds-type--display-4'>No products available</h2>
      </div>
    );

  const productIdList = unlabelledProductIds.concat(labelledProductIds);
  if (productId) {
    const productIdx = productIdList.findIndex((id) => id === productId);
    const prevProductIdx =
      productIdx === 0 ? productIdList.length - 1 : productIdx - 1;
    const nextProductIdx =
      productIdx === productIdList.length - 1 ? 0 : productIdx + 1;
    return (
      <AllProductContext.Provider
        value={{
          totalProductAmount: productIdList.length,
          labelledProductAmount: labelledProductAmount,
          prevProductId: productIdList[prevProductIdx],
          nextProductId: productIdList[nextProductIdx],
          isCurrentLabelled: labelledProductIds.includes(productId),
          increaseLabelledAmount,
        }}
      >
        <Outlet />
      </AllProductContext.Provider>
    );
  }

  const selectProductId = productIdList[0];
  return (
    <Navigate
      to={`/labelling/datasets/${datasetId}/products/${selectProductId}`}
    />
  );
}
